import { LocaleService } from '../components/zds-config/locale-service';
import { getSlottedElements } from './slotted';

export interface ValidationEvent {
	value: unknown;
	viewValue: unknown;
	validationState?: ValidationState;
}

// We use 'any' here to prevent type errors for products
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ValidationFunction = (value: any, viewValue: any) => ValidationState;

export type ValidationMessages = Record<string, string>;

export enum ValidationReasons {
	PatternMismatch = 'patternMismatch',
	RangeOverflow = 'rangeOverflow',
	RangeUnderflow = 'rangeUnderflow',
	StepMismatch = 'stepMismatch',
	TooLong = 'tooLong',
	TooShort = 'tooShort',
	TypeMismatch = 'typeMismatch',
	ValueMissing = 'valueMissing',
	InvalidEmail = 'invalidEmail',
}

export interface ValidationState {
	message: string;
	reason: ValidationReasons | string;
	hidden?: boolean;
	valid: boolean;
}

export enum VisualValidityStatus {
	Valid,
	InvalidUnfocused,
	InvalidFocus,
}

export function getDefaultMessage(reason: ValidationState['reason']): string {
	return LocaleService.t(`validation.${reason}`);
}

function getInvalidReason(validity: Partial<ValidityState>): ValidationState['reason'] {
	for (const key in validity) {
		if (key !== 'valid' && validity[key]) return key;
	}

	return null;
}

export function addValidation(validationFunctions: ValidationFunction[], validationFunction: ValidationFunction): void {
	if (!validationFunctions.includes(validationFunction)) {
		validationFunctions.push(validationFunction);
	}
}

export function removeValidation(
	validationFunctions: ValidationFunction[],
	validationFunction: ValidationFunction,
): ValidationFunction[] {
	return validationFunctions.filter((currentFunction) => currentFunction !== validationFunction);
}

export function getValidationCustomMessages(hostElement: HTMLElement): ValidationMessages {
	const customMessages = {};

	Array.from(hostElement.querySelectorAll('[slot="validation-message"]')).forEach((element: HTMLElement) => {
		let validationMessageElements: HTMLElement[];
		if (element.nodeName === 'SLOT') {
			validationMessageElements = getSlottedElements(element as HTMLSlotElement)
				.filter((node) => node.nodeType === 1) // Filter node type ELEMENT_NODE
				.map((node) => node);
		} else {
			validationMessageElements = [element];
		}

		validationMessageElements.forEach((validationMessageElement) => {
			const reason = validationMessageElement.getAttribute('data-reason');

			if (!reason) {
				return;
			}

			customMessages[reason] = validationMessageElement.innerText;
		});
	});

	return customMessages;
}

export function getValidationState(validity: Partial<ValidityState>): ValidationState {
	const reason = getInvalidReason(validity);

	return {
		message: getDefaultMessage(reason),
		reason,
		valid: validity.valid,
	};
}

export function validate(
	validationFunctions: ValidationFunction[],
	validationCustomMessages: ValidationMessages,
	value: unknown,
	viewValue: unknown,
	disabled: boolean,
	required: boolean,
	validateValueMissingFunction: ValidationFunction = validateVisibleValue,
): ValidationState {
	let validationState: ValidationState = {
		message: '',
		reason: '',
		valid: true,
	};

	if (disabled) {
		return validationState;
	}

	const modifiedValidationFunctions = [...validationFunctions];
	if (required) {
		modifiedValidationFunctions.push(validateValueMissingFunction);
	}

	// Return the first invalid validation state, is there is an invalid one
	let isInvalidHidden = false;
	for (const validationFunction of modifiedValidationFunctions) {
		const thisValidationState = validationFunction(value, viewValue ?? value);
		if (!thisValidationState.valid) {
			if (thisValidationState.hidden) {
				isInvalidHidden = true;
			} else {
				validationState = thisValidationState;
				break;
			}
		}
	}

	if (validationState.valid && isInvalidHidden) {
		validationState = {
			message: '',
			reason: '',
			valid: false,
		};
	}

	// If the current input field is marked as invalid, find out whether the default message should be overwritten
	// with a given custom message
	if (!validationState.valid && validationCustomMessages[validationState.reason]) {
		validationState.message = validationCustomMessages[validationState.reason];
	}

	return validationState;
}

// Makes sure the given value is not a whitespace character, used as a supplement to the ValidationState valueMissing
// required validation
export function validateVisibleValue(value: unknown) {
	return {
		message: getDefaultMessage('valueMissing'),
		reason: ValidationReasons.ValueMissing,
		valid: typeof value === 'string' && value.trim() !== '',
	};
}

export function validateMaxLength<T extends { maxlength: number }>(this: T, value: string) {
	return {
		message: getDefaultMessage(ValidationReasons.TooLong),
		reason: ValidationReasons.TooLong,
		valid: this.maxlength === undefined || value.length <= this.maxlength,
	};
}

export function validateMinLength<T extends { minlength: number }>(this: T, value: string) {
	return {
		message: getDefaultMessage(ValidationReasons.TooShort),
		reason: ValidationReasons.TooShort,
		valid: this.minlength === undefined || value.length >= this.minlength || value.length === 0,
	};
}

interface GetVisualValidityStatusArgs {
	validationState: ValidationState;
	touched: boolean;
	focused: boolean;
}

export function getVisualValidityStatus({
	validationState,
	touched,
	focused,
}: GetVisualValidityStatusArgs): VisualValidityStatus {
	if (validationState.valid || !touched) return VisualValidityStatus.Valid;
	if (focused) return VisualValidityStatus.InvalidFocus;
	return VisualValidityStatus.InvalidUnfocused;
}
